export type GuideSection = {
  heading: string;
  paragraphs: string[];
  list?: string[];
};

export type Guide = {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt: string;
  intro: string;
  sections: GuideSection[];
};

export const GUIDES: Guide[] = [
  {
    slug: "calcular-custo-pedagio-viagem",
    title: "Como calcular o custo de pedágio de uma viagem no Brasil",
    description:
      "Aprenda a estimar o gasto com pedágio antes de viajar: onde consultar tarifas oficiais, como a cobrança por eixos funciona, um exemplo completo e o que pode mudar o valor final.",
    publishedAt: "2026-09-06",
    updatedAt: "2026-09-15",
    intro:
      "Saber quanto você vai gastar de pedágio antes de sair de casa evita surpresas e ajuda a montar o orçamento real da viagem. Neste guia explicamos como a cobrança funciona nas rodovias brasileiras, mostramos um cálculo completo passo a passo e listamos os fatores que fazem o valor real ficar diferente da estimativa.",
    sections: [
      {
        heading: "Onde as tarifas de pedágio são definidas",
        paragraphs: [
          "No Brasil, as tarifas de pedágio não são aleatórias: cada praça tem um valor oficial definido em contrato de concessão e reajustado periodicamente. Nas rodovias federais, a fonte oficial é a ANTT (Agência Nacional de Transportes Terrestres). Nas estaduais, cada estado tem seu órgão regulador — em São Paulo, por exemplo, é a ARTESP.",
          "Essas tarifas são públicas e ficam estáveis por meses. O reajuste costuma acontecer uma vez por ano, em data prevista no contrato da concessionária. É por isso que uma boa estimativa de pedágio, baseada nas tabelas oficiais, tende a ser muito próxima do valor que você paga na praça.",
        ],
      },
      {
        heading: "Como a cobrança por categoria de veículo funciona",
        paragraphs: [
          "O valor do pedágio depende da categoria do veículo. Carros de passeio pagam a tarifa base. Motocicletas normalmente pagam 50% da tarifa base. Veículos comerciais — caminhões e ônibus — pagam um valor multiplicado pelo número de eixos: um caminhão de 3 eixos paga, em geral, 3 vezes a tarifa base por eixo.",
          "Algumas concessionárias têm regras especiais, como desconto para quem usa tag de cobrança automática ou tarifas diferentes por sentido. Por isso, trate qualquer estimativa como um valor de referência e confira o valor vigente antes de viagens críticas.",
        ],
        list: [
          "Carro de passeio: tarifa base da praça",
          "Moto: em geral 50% da tarifa base",
          "Caminhão e ônibus: tarifa multiplicada pelo número de eixos",
          "Tag (Sem Parar, ConectCar etc.): pode ter desconto conforme a concessionária",
        ],
      },
      {
        heading: "Por que estimar praça por praça",
        paragraphs: [
          "Chutar uma média por quilômetro rodado é o erro mais comum. O correto é somar praça por praça ao longo do trajeto, porque duas rotas com a mesma distância podem ter custos de pedágio completamente diferentes — uma pode cruzar cinco praças e a outra nenhuma.",
          "O TrajetoFácil faz exatamente isso: cruza a geometria da rota com a base de praças oficiais (ANTT e concessionárias estaduais) e soma as tarifas da categoria do seu veículo, separando ida e volta. Assim você vê não só o total, mas quais praças estão no caminho e quanto custa cada uma.",
        ],
      },
      {
        heading: "Exemplo prático: São Paulo a Ribeirão Preto de carro",
        paragraphs: [
          "Imagine um trajeto de cerca de 315 km pela Anhanguera, com quatro praças no sentido de ida cujas tarifas de carro somam, hipoteticamente, R$ 12,40 + R$ 10,80 + R$ 9,60 + R$ 11,20. O pedágio da ida fica em R$ 44,00. Se todas as praças cobram nos dois sentidos, a volta repete o valor e a viagem redonda soma R$ 88,00.",
          "Agora o combustível: 315 km de ida mais 315 km de volta dão 630 km. Com um carro que faz 12 km/l e gasolina a R$ 5,89, são 52,5 litros e cerca de R$ 309,00. O custo variável total da viagem redonda fica em torno de R$ 397,00, ou aproximadamente R$ 0,63 por quilômetro.",
          "Troque o carro por um caminhão de 3 eixos e só o pedágio triplica: R$ 132,00 na ida, R$ 264,00 na viagem redonda. É por isso que a categoria do veículo pesa tanto no orçamento.",
        ],
        list: [
          "Pedágio ida: R$ 44,00 · volta: R$ 44,00 · total R$ 88,00",
          "Combustível: 630 km ÷ 12 km/l × R$ 5,89 ≈ R$ 309,00",
          "Custo variável total ≈ R$ 397,00 (≈ R$ 0,63/km)",
        ],
      },
      {
        heading: "Exceções e cuidados que mudam o valor",
        paragraphs: [
          "Estimativa e valor pago podem divergir por motivos legítimos. Vale conferir sempre que a precisão for crítica.",
        ],
        list: [
          "Tag de cobrança automática: algumas concessionárias dão desconto por uso ou por frequência;",
          "Reajuste recente: uma tarifa alterada há poucos dias pode ainda não estar refletida;",
          "Praça que cobra em um único sentido: a volta pode custar menos que a ida;",
          "Pórtico free flow: cobrança por trecho percorrido, sem cancela, com regra própria;",
          "Eixo suspenso em caminhão vazio: reduz a contagem de eixos cobrados em várias concessões;",
          "Desvio de rota no caminho: mudar de rodovia muda o conjunto de praças.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Informe origem e destino, marque ida e volta, escolha a categoria do veículo (e o número de eixos, se for caminhão ou ônibus), preencha preço do litro e km/l e clique em buscar rota. O resultado traz distância, tempo, combustível e pedágio de cada sentido, com o detalhe praça por praça e o custo total da viagem.",
        ],
      },
    ],
  },
  {
    slug: "estimar-gasto-combustivel-kml",
    title: "Combustível: como estimar o gasto da viagem por km/l e preço do litro",
    description:
      "Fórmula simples para calcular quanto você vai gastar de combustível em qualquer viagem: distância, consumo (km/l) e preço do litro, com exemplo completo e fatores que alteram o resultado.",
    publishedAt: "2026-09-06",
    updatedAt: "2026-09-15",
    intro:
      "O combustível é quase sempre o maior custo variável de uma viagem de carro. A boa notícia: a conta para estimá-lo é simples e funciona para qualquer trajeto. Veja a fórmula, um exemplo completo e os cuidados para o número ficar próximo da realidade.",
    sections: [
      {
        heading: "A fórmula básica",
        paragraphs: [
          "O cálculo é: litros necessários = distância total ÷ consumo do veículo (km/l). Depois, gasto = litros × preço do litro. Exemplo: em uma viagem de 400 km com um carro que faz 12 km/l e gasolina a R$ 5,89, você precisa de cerca de 33,3 litros e gasta aproximadamente R$ 196.",
          "Para ida e volta, basta dobrar a distância — ou calcular cada sentido separadamente, como faz o TrajetoFácil, já que o trajeto de volta pode não ser idêntico.",
        ],
        list: [
          "Litros = distância (km) ÷ consumo (km/l)",
          "Gasto = litros × preço do litro (R$)",
          "Exemplo: 400 km ÷ 12 km/l × R$ 5,89 ≈ R$ 196",
        ],
      },
      {
        heading: "Descubra o consumo real do seu veículo",
        paragraphs: [
          "O consumo de fábrica raramente se repete na estrada. Para medir o seu, encha o tanque, zere o odômetro parcial, rode normalmente e, na próxima parada, divida os quilômetros rodados pelos litros abastecidos. Repita em duas ou três viagens para ter uma média confiável.",
          "Lembre-se de que o consumo muda conforme o cenário: estrada plana, serra, ar-condicionado ligado, carga e velocidade influenciam bastante. Um carro que faz 14 km/l na estrada pode cair para 10 km/l na cidade ou em serra com carga.",
        ],
      },
      {
        heading: "Exemplo prático: mesma viagem, três consumos",
        paragraphs: [
          "Uma viagem de 480 km só de ida, com gasolina a R$ 6,09, muda bastante conforme o veículo. Com 14 km/l são 34,3 litros e R$ 208,90. Com 11 km/l sobem para 43,6 litros e R$ 265,50. Com 8 km/l (SUV carregado em serra) chegam a 60 litros e R$ 365,40.",
          "A diferença entre o melhor e o pior cenário é de R$ 156,50 em um único sentido — mais de R$ 310 na viagem redonda. É por isso que usar o consumo real, e não o de catálogo, muda o orçamento.",
        ],
        list: [
          "14 km/l → 34,3 L → R$ 208,90",
          "11 km/l → 43,6 L → R$ 265,50",
          "8 km/l → 60,0 L → R$ 365,40",
        ],
      },
      {
        heading: "Fatores e exceções que alteram o gasto",
        paragraphs: [
          "Velocidade é o maior vilão: acima de 110-120 km/h o consumo sobe de forma acentuada. Pneus descalibrados, bagageiro de teto, excesso de peso e acelerações bruscas também pesam. Em viagens longas, manter velocidade constante e pneus calibrados pode economizar um tanque inteiro na ida e volta.",
        ],
        list: [
          "Preço do litro varia entre estados e entre rodovia e cidade — use o preço que você realmente vai pagar;",
          "Etanol rende menos por litro: compare o custo por quilômetro, não o preço da bomba;",
          "Trechos de serra, chuva e vento contrário aumentam o consumo;",
          "Trânsito parado em trecho urbano derruba a média;",
          "Reboque, bagageiro e veículo cheio podem tirar 10% a 20% da autonomia.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Preencha o preço do litro e o km/l medido do seu veículo, informe origem e destino e marque ida e volta. A calculadora aplica a fórmula em cada trecho e mostra o combustível separado do pedágio, para você ver exatamente de onde vem cada real do custo.",
          "Se você não sabe o consumo e prefere trabalhar com um valor fechado por quilômetro, deixe preço do litro e km/l em branco e use apenas o campo de valor por km: o sistema multiplica a distância pelo valor informado.",
        ],
      },
    ],
  },
  {
    slug: "pedagio-por-eixos-caminhoes",
    title: "Pedágio por eixos: como funciona a cobrança para caminhões e ônibus",
    description:
      "Entenda a cobrança de pedágio por número de eixos, as categorias de veículos comerciais, um exemplo de custo de frete e as exceções como eixo suspenso e pórtico free flow.",
    publishedAt: "2026-09-06",
    updatedAt: "2026-09-15",
    intro:
      "Para caminhões e ônibus, o pedágio não é um valor fixo por praça: ele é multiplicado pelo número de eixos do veículo. Entender essa regra é essencial para quem precifica frete ou planeja o custo de uma frota.",
    sections: [
      {
        heading: "A regra da multiplicação por eixo",
        paragraphs: [
          "Nas concessões brasileiras, a tarifa de veículos comerciais segue a chamada tarifa por eixo: a praça cobra a tarifa base multiplicada pelo número de eixos. Um caminhão toco (2 eixos) paga 2× a tarifa base; um bitrem de 7 eixos paga 7×. Em uma praça de R$ 15, a diferença entre um carro e um bitrem é de R$ 15 para R$ 105 — por praça.",
          "Ônibus seguem a mesma lógica: em geral têm 2 ou mais eixos e pagam conforme a configuração. Motocicletas, na ponta oposta, costumam pagar metade da tarifa base.",
        ],
        list: [
          "Carro de passeio: 1× tarifa base",
          "Moto: 0,5× tarifa base (na maioria das concessões)",
          "Caminhão/ônibus: tarifa base × número de eixos",
          "Eixo suspenso (erguido) conta conforme a regra da concessionária",
        ],
      },
      {
        heading: "Por que isso muda tudo no custo do frete",
        paragraphs: [
          "Em um corredor com muitas praças — como os eixos São Paulo–Rio ou São Paulo–Curitiba — o pedágio de um caminhão pesado pode ultrapassar o próprio combustível. Ignorar a contagem correta de eixos é um dos erros mais caros na precificação de fretes.",
          "Por isso, ao calcular o custo de uma operação, informe sempre o número exato de eixos da composição. Um erro de um eixo em uma rota com dez praças já distorce o preço final de forma relevante.",
        ],
      },
      {
        heading: "Exemplo prático: o mesmo trecho em quatro veículos",
        paragraphs: [
          "Considere uma rota com seis praças cuja soma da tarifa base de carro é R$ 78,00 em um sentido. Na mesma rota, a moto pagaria cerca de R$ 39,00; um caminhão toco de 2 eixos, R$ 156,00; um carreta de 5 eixos, R$ 390,00; e um bitrem de 7 eixos, R$ 546,00.",
          "Em uma operação de ida e volta carregada, o bitrem chega a R$ 1.092,00 só de pedágio. Some o combustível — 700 km a 2,5 km/l com diesel a R$ 6,20 dão cerca de R$ 1.736,00 — e o custo variável passa de R$ 2.800,00. Precificar esse frete sem calcular os eixos corretos é convite ao prejuízo.",
        ],
        list: [
          "Moto: R$ 39,00 · Carro: R$ 78,00",
          "Caminhão 2 eixos: R$ 156,00 · 5 eixos: R$ 390,00 · 7 eixos: R$ 546,00",
          "Ida e volta do bitrem: R$ 1.092,00 apenas de pedágio",
        ],
      },
      {
        heading: "Exceções: eixo suspenso, free flow e cobrança por sentido",
        paragraphs: [
          "Caminhões têm regras específicas que reduzem ou alteram a cobrança. Vale conhecê-las antes de fechar o preço de uma operação.",
        ],
        list: [
          "Eixo suspenso: caminhão vazio com eixo erguido paga por menos eixos em boa parte das concessões;",
          "Pórtico free flow: cobrança eletrônica por trecho, sem cancela, com tarifação proporcional;",
          "Praça de sentido único: o custo pode aparecer só na ida ou só na volta;",
          "Tarifa de veículos especiais e excesso de peso pode ter regra própria;",
          "Programas de desconto por frequência para transportadoras variam por concessionária.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Selecione caminhão ou ônibus, informe o número de eixos e a calculadora aplica automaticamente a tarifa correta em cada praça da rota, usando as tabelas oficiais da ANTT e das concessionárias estaduais. O resultado mostra o total e o detalhe praça por praça, para você conferir antes de fechar o frete.",
        ],
      },
    ],
  },
  {
    slug: "pedagio-ida-volta-sentidos-diferentes",
    title: "Ida e volta: por que o pedágio pode ser diferente em cada sentido",
    description:
      "Nem sempre a volta custa igual à ida. Entenda cobrança por sentido, praças gêmeas, pórticos free flow, veja um exemplo numérico e planeje o custo da viagem redonda.",
    publishedAt: "2026-09-06",
    updatedAt: "2026-09-15",
    intro:
      "Muita gente assume que a volta custa o mesmo que a ida — e erra no orçamento. Cobrança por sentido, praças localizadas em apenas uma direção e sistemas de pórtico fazem o valor mudar. Veja quando isso acontece e como não ser pego de surpresa.",
    sections: [
      {
        heading: "Praças que cobram em um único sentido",
        paragraphs: [
          "Algumas praças de pedágio cobram apenas em uma direção da rodovia. Nesses casos, o custo da ida inclui a praça e o da volta não — ou o contrário. É comum a concessionária concentrar a cobrança em um sentido e embutir no preço o custo dos dois trechos.",
          "Há também o caso das praças gêmeas: duas estruturas próximas, uma para cada sentido, com a mesma tarifa. Aqui o custo se repete na ida e na volta, mas é importante não contar as duas no mesmo sentido — um erro comum em estimativas mal feitas.",
        ],
      },
      {
        heading: "Pórticos free flow e cobrança eletrônica",
        paragraphs: [
          "Os pórticos de cobrança eletrônica (free flow) substituem a praça tradicional: não há cancela, e o valor é cobrado por tag ou por leitura de placa. Eles podem existir em pontos diferentes da rodovia para cada sentido, o que também muda onde — e às vezes quanto — você paga em cada direção.",
        ],
      },
      {
        heading: "Rotas alternativas na volta",
        paragraphs: [
          "Outro motivo de diferença: a melhor rota de volta nem sempre é o espelho da ida. Conversões, acessos e retornos podem levar o caminho de volta por trechos com mais ou menos praças. Por isso, calcular ida e volta como duas rotas independentes é mais preciso do que simplesmente dobrar o valor da ida.",
        ],
      },
      {
        heading: "Exemplo prático: quando dobrar a ida dá errado",
        paragraphs: [
          "Em um trajeto hipotético de 240 km, a ida cruza três praças: R$ 14,20, R$ 9,70 e R$ 8,50 — total de R$ 32,40. Na volta, uma dessas praças cobra apenas no sentido oposto e não se repete, e o retorno usa um acesso que evita outra. A volta soma R$ 14,20.",
          "Quem dobra a ida orça R$ 64,80 e superestima o custo em R$ 18,20. Quem calcula os dois sentidos separadamente chega a R$ 46,60 — o valor real. Em viagens frequentes, essa diferença repetida distorce todo o controle de despesas.",
        ],
        list: [
          "Ida: R$ 32,40 · Volta: R$ 14,20 · Real: R$ 46,60",
          "Estimativa por dobra da ida: R$ 64,80 (erro de R$ 18,20)",
        ],
      },
      {
        heading: "Cuidados adicionais",
        paragraphs: [
          "Além da assimetria entre sentidos, dois pontos merecem atenção: paradas intermediárias alteram o caminho e podem introduzir praças novas, e horários de tarifa diferenciada existem em algumas concessões urbanas. Confira o detalhamento por praça sempre que o valor parecer fora do esperado.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Ao ativar a opção ida e volta, o TrajetoFácil calcula cada sentido separadamente e soma as praças de cada trajeto, mostrando os totais lado a lado. Assim você vê de imediato se a volta custa mais, menos ou igual à ida — e monta o orçamento com os dois valores corretos.",
        ],
      },
    ],
  },
  {
    slug: "precificar-frete-custo-por-km",
    title: "Como precificar um frete ou viagem usando o custo por km",
    description:
      "Calcule o custo por quilômetro de uma operação (combustível + pedágio), aplique o multiplicador de margem e chegue ao preço certo do transporte, com exemplo completo.",
    publishedAt: "2026-09-06",
    updatedAt: "2026-09-15",
    intro:
      "Cobrar por km sem saber o seu custo real por km é o caminho mais curto para prejuízo. Este guia mostra como montar o custo por quilômetro da operação e transformar isso em preço com margem.",
    sections: [
      {
        heading: "Passo 1: some os custos variáveis da viagem",
        paragraphs: [
          "Os custos variáveis são os que só existem quando a viagem acontece: combustível e pedágio. Calcule o combustível pela fórmula distância ÷ consumo × preço do litro, e estime o pedágio praça por praça conforme a categoria e os eixos do veículo. A soma dos dois é o custo variável da viagem.",
          "Para operações frequentes, vale incluir também uma reserva por quilômetro para manutenção e pneus — itens que desgastam proporcionalmente à distância rodada.",
        ],
        list: [
          "Combustível: distância ÷ km/l × preço do litro",
          "Pedágio: soma das praças da rota, por categoria/eixos",
          "Reserva de manutenção e pneus por km (opcional, recomendado)",
        ],
      },
      {
        heading: "Passo 2: divida pelo total de quilômetros",
        paragraphs: [
          "Com o custo total da viagem em mãos, divida pela distância total (incluindo a volta, se o veículo retorna vazio ou às suas custas). Esse é o seu custo por km — o piso abaixo do qual você não pode cobrar.",
          "Exemplo simplificado: uma viagem de 500 km com R$ 350 de combustível e R$ 150 de pedágio custa R$ 500 no total, ou seja, R$ 1,00 por km. Cobrar menos que isso é pagar para trabalhar.",
        ],
      },
      {
        heading: "Passo 3: aplique a margem com um multiplicador",
        paragraphs: [
          "O preço final nasce de um multiplicador sobre o custo: um fator de 1,30 significa 30% de margem sobre o custo; 1,50 significa 50%. O multiplicador certo depende dos seus custos fixos (salário, seguro, depreciação) e do lucro esperado, mas a lógica é sempre a mesma: custo × fator = preço.",
          "No TrajetoFácil, o campo multiplicador de repasse faz essa conta automaticamente: você vê o custo puro, o valor a cobrar por km e o total a repassar, já com a margem aplicada — útil tanto para fretes quanto para ratear uma viagem entre amigos ou cobrar um deslocamento de cliente.",
        ],
      },
      {
        heading: "Exemplo prático: frete de 620 km ida e volta",
        paragraphs: [
          "Caminhão de 3 eixos, 620 km de ida e 620 km de volta, total de 1.240 km. Diesel a R$ 6,20 com consumo de 3,2 km/l dá 387,5 litros e R$ 2.402,50 de combustível. O pedágio somado das praças dos dois sentidos, na categoria de 3 eixos, fecha em R$ 486,00.",
          "Custo variável: R$ 2.888,50, ou R$ 2,33 por quilômetro. Somando uma reserva de R$ 0,35/km para manutenção e pneus, o custo sobe para R$ 2,68/km, ou R$ 3.322,50. Com multiplicador de 1,35, o preço de venda fica em R$ 4.485,40 — cerca de R$ 3,62 por quilômetro.",
        ],
        list: [
          "Combustível: R$ 2.402,50 · Pedágio: R$ 486,00",
          "Custo variável: R$ 2.888,50 (R$ 2,33/km)",
          "Com reserva de manutenção: R$ 3.322,50 (R$ 2,68/km)",
          "Preço com fator 1,35: R$ 4.485,40 (R$ 3,62/km)",
        ],
      },
      {
        heading: "Erros comuns e cuidados na precificação",
        paragraphs: [
          "O primeiro erro é esquecer a volta: se o retorno é por sua conta, o custo da viagem redonda precisa entrar no preço da ida. O segundo é usar consumo de fábrica em vez do consumo real. O terceiro é ignorar a diferença de pedágio por sentido e por categoria de veículo.",
        ],
        list: [
          "Não confundir margem sobre custo com desconto sobre preço;",
          "Rever o preço do combustível a cada cotação — ele muda toda semana;",
          "Considerar tempo de espera, carga e descarga em operações longas;",
          "Guardar o link da simulação junto da proposta, para justificar o valor.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Monte a rota com ida e volta, escolha o veículo e os eixos, preencha combustível e informe o multiplicador. A calculadora mostra custo, valor por km e total a repassar. Depois, use o botão de compartilhar para enviar a simulação ao cliente com os mesmos parâmetros.",
        ],
      },
    ],
  },
  {
    slug: "custo-por-km-na-pratica",
    title: "Custo por quilômetro: o que é, como calcular e como usar no dia a dia",
    description:
      "O custo por km transforma qualquer viagem em um número comparável. Veja como calcular, o que incluir, exemplos por tipo de veículo e como usar o indicador para decidir.",
    publishedAt: "2026-09-15",
    updatedAt: "2026-09-15",
    intro:
      "Distância, combustível e pedágio dizem pouco isoladamente. O custo por quilômetro junta tudo em um número só, que permite comparar rotas, veículos e propostas com a mesma régua. Este guia mostra como montar o indicador e usá-lo.",
    sections: [
      {
        heading: "O que é custo por quilômetro",
        paragraphs: [
          "Custo por km é o total gasto na viagem dividido pela distância percorrida. Se uma viagem de 300 km custou R$ 270 entre combustível e pedágio, o custo por km é R$ 0,90. É o indicador que permite comparar situações diferentes: dois veículos, duas rotas, dois períodos do ano.",
          "Ele também é a base de qualquer cobrança por deslocamento. Reembolsos de empresa, fretes e serviços com visita técnica quase sempre são negociados em reais por quilômetro.",
        ],
      },
      {
        heading: "O que incluir na conta",
        paragraphs: [
          "Comece pelos custos variáveis, que são obrigatórios: combustível e pedágio. Depois decida se a sua análise precisa de custos de desgaste e custos fixos rateados. Para uma viagem pessoal, os variáveis bastam; para uma operação profissional, incluir desgaste e fixos evita subprecificação.",
        ],
        list: [
          "Sempre: combustível e pedágio da rota;",
          "Recomendado em uso profissional: pneus, manutenção e óleo por km;",
          "Em frota: depreciação, seguro, licenciamento e rastreamento rateados;",
          "Opcional: estacionamento, alimentação e pernoite em viagens longas.",
        ],
      },
      {
        heading: "Exemplo prático: três perfis na mesma rota de 400 km",
        paragraphs: [
          "Rota de 400 km com R$ 52,00 de pedágio para carro. Um carro popular a 13 km/l com gasolina a R$ 5,99 gasta R$ 184,30 de combustível; total R$ 236,30, ou R$ 0,59/km. Um SUV a 9 km/l gasta R$ 266,20; total R$ 318,20, ou R$ 0,80/km.",
          "Um caminhão de 3 eixos paga R$ 156,00 de pedágio e, a 3,2 km/l com diesel a R$ 6,20, gasta R$ 775,00 de combustível; total R$ 931,00, ou R$ 2,33/km — quatro vezes o custo do carro popular na mesma estrada.",
        ],
        list: [
          "Carro popular: R$ 236,30 → R$ 0,59/km",
          "SUV: R$ 318,20 → R$ 0,80/km",
          "Caminhão 3 eixos: R$ 931,00 → R$ 2,33/km",
        ],
      },
      {
        heading: "Como usar o indicador para decidir",
        paragraphs: [
          "Com o custo por km você responde perguntas práticas: vale pegar a rota mais curta com pedágio ou a mais longa sem? Compensa usar o carro próprio ou alugar? Qual o valor mínimo para aceitar um deslocamento? Basta comparar o custo por km de cada alternativa na mesma viagem.",
          "Um cuidado: o custo por km cai em viagens longas de estrada e sobe em trajetos curtos e urbanos, porque o consumo piora e o trecho tem menos quilômetros para diluir os custos fixos. Compare sempre cenários semelhantes.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Calcule a rota com ida e volta e veja o custo total. Se você já trabalha com um valor fechado por quilômetro, deixe preço do litro e km/l em branco e preencha apenas o campo de valor por km — a calculadora multiplica a distância pelo valor e mantém o pedágio separado, para você ver as duas parcelas.",
        ],
      },
    ],
  },
  {
    slug: "multiplas-paradas-e-roteirizacao",
    title: "Rotas com várias paradas: como planejar entregas e viagens com escalas",
    description:
      "Saiba como montar uma rota com múltiplos pontos, em que ordem visitar cada parada, como o custo se acumula por trecho e quando vale dividir em duas viagens.",
    publishedAt: "2026-09-15",
    updatedAt: "2026-09-15",
    intro:
      "Poucas viagens são um trecho só. Entregas, visitas a clientes e viagens em família com escalas envolvem vários pontos, e a ordem em que você visita cada um muda distância, tempo e pedágio. Veja como planejar.",
    sections: [
      {
        heading: "Cada trecho é uma rota independente",
        paragraphs: [
          "Uma rota com paradas é, na prática, uma sequência de trechos: origem até a parada 1, parada 1 até a parada 2, e assim por diante. Cada trecho tem sua própria distância, seu próprio consumo de combustível e suas próprias praças de pedágio. O custo total é a soma dos trechos.",
          "Isso importa porque um trecho pode entrar em uma rodovia pedagiada e outro não. Somar médias esconde esse detalhe; somar trecho a trecho revela onde está o custo.",
        ],
      },
      {
        heading: "A ordem das paradas muda o custo",
        paragraphs: [
          "Visitar A, B e C não custa o mesmo que visitar C, B e A. Com três paradas já existem várias sequências possíveis, e a diferença entre a melhor e a pior costuma ser significativa em quilômetros e em praças cruzadas.",
          "Regras práticas ajudam: agrupe pontos por região, evite cruzar a mesma rodovia duas vezes, e deixe para o fim a parada mais próxima do ponto de retorno. Depois, teste as sequências candidatas e compare o custo total.",
        ],
        list: [
          "Agrupe paradas por região ou corredor rodoviário;",
          "Evite zigue-zague: cada retorno desnecessário custa km e pedágio;",
          "Considere janelas de horário de cada parada antes de otimizar distância;",
          "Deixe a parada mais próxima da base por último.",
        ],
      },
      {
        heading: "Exemplo prático: três entregas em um dia",
        paragraphs: [
          "Saindo da base, três entregas: A a 60 km, B a 145 km e C a 95 km, todas no mesmo corredor. Na ordem A → B → C → base, a distância total fica em 390 km. Na ordem A → C → B → base, ela cai para 330 km.",
          "Com um utilitário a 9 km/l e diesel a R$ 6,20, os 60 km economizados valem R$ 41,30 de combustível. Se a sequência pior ainda cruza duas praças extras de R$ 11,40, a economia sobe para R$ 64,10 — em um único dia de operação.",
        ],
        list: [
          "Sequência A → B → C: 390 km",
          "Sequência A → C → B: 330 km (−60 km)",
          "Economia estimada no dia: R$ 64,10",
        ],
      },
      {
        heading: "Cuidados e exceções",
        paragraphs: [
          "Otimizar só por distância pode quebrar a operação. Janelas de recebimento, tempo de carga e descarga, jornada do motorista e restrições de circulação para caminhões em áreas urbanas às vezes obrigam a escolher uma sequência mais cara em quilômetros e mais barata em tempo.",
          "Em roteiros muito longos, avalie dividir em duas viagens: o custo de pernoite pode ser menor que o de um retorno vazio de centenas de quilômetros.",
        ],
      },
      {
        heading: "Faça no TrajetoFácil",
        paragraphs: [
          "Adicione até seis pontos na calculadora, na ordem que quiser testar, e veja distância, tempo, combustível e pedágio acumulados. Para comparar sequências, calcule uma, copie o link de compartilhamento, reordene as paradas e calcule de novo: os dois links guardam as duas simulações lado a lado.",
        ],
      },
    ],
  },
  {
    slug: 'pedagio-sao-paulo-rio-de-janeiro',
    title: 'Pedágio de São Paulo ao Rio de Janeiro: quanto custa a viagem pela Dutra',
    description: 'Quanto custa o pedágio e o combustível de São Paulo ao Rio de Janeiro de carro: R$ 66,00 na ida, R$ 75,60 na volta, distância, tempo e custo total da viagem.',
    publishedAt: "2026-10-04",
    updatedAt: "2026-10-04",
    intro: 'São Paulo–Rio é uma das viagens de carro mais comuns do país, quase toda pela Via Dutra (BR-116). Neste guia mostramos quanto custa o pedágio em cada sentido, quanto se gasta de combustível e o custo total de ida e volta, além de explicar por que a volta sai mais cara que a ida.',
    sections: [
      {
        heading: 'Resumo dos números',
        paragraphs: [
          'Todos os números deste guia saíram da calculadora do TrajetoFácil em 4 de outubro de 2026, para carro de passeio, com gasolina a R$ 5,89 por litro e consumo de 10 km/l. Pedágio é estimativa pelas tarifas oficiais vigentes; combustível muda conforme o preço que você paga e o consumo real do seu carro.',
        ],
        list: [
          'Ida (São Paulo → Rio): 442,8 km, cerca de 5h41, 6 praças, pedágio de R$ 66,00',
          'Volta (Rio → São Paulo): 428,6 km, cerca de 5h23, 8 praças, pedágio de R$ 75,60',
          'Combustível de ida e volta: R$ 513,26',
          'Pedágio de ida e volta: R$ 141,60',
          'Custo total da viagem: R$ 654,86, cerca de R$ 0,75 por km',
        ],
      },
      {
        heading: 'Por que a volta custa mais que a ida',
        paragraphs: [
          'Na Dutra, parte das praças cobra só em um sentido, e há praças de valores diferentes em cada pista. Por isso o trajeto de volta passa por 8 praças, contra 6 na ida, e o pedágio sobe de R$ 66,00 para R$ 75,60. O caminho exato que a rota faz na saída de cada cidade também muda um pouco a distância: 442,8 km na ida contra 428,6 km na volta.',
          'É por isso que o TrajetoFácil calcula ida e volta como duas viagens separadas, em vez de dobrar o valor da ida. Dobrar a ida daria R$ 132,00 de pedágio, R$ 9,60 a menos que o valor real estimado.',
        ],
      },
      {
        heading: 'Quanto o pedágio pesa no orçamento',
        paragraphs: [
          'Nessa viagem o pedágio representa cerca de 22% do custo total, e o combustível, 78%. Isso significa que o consumo do carro faz mais diferença que o pedágio: um carro que faz 13 km/l em vez de 10 economiza cerca de R$ 118 em combustível na ida e volta, quase o mesmo valor de todo o pedágio da viagem.',
        ],
      },
      {
        heading: 'Dicas para a viagem',
        paragraphs: [
          'Saia fora dos horários de pico da Marginal e da Avenida Brasil para chegar perto do tempo estimado. Se tiver tag de pagamento automático, confira se a concessionária dá desconto para quem usa tag. Para moto, o pedágio costuma ser metade do valor do carro, e caminhões e ônibus pagam por eixo.',
        ],
      },
      {
        heading: 'Refaça a conta com os seus números',
        paragraphs: [
          'Abra a calculadora, digite origem e destino, escolha o seu veículo (moto, carro, ônibus ou caminhão com o número de eixos) e informe o preço do litro e o consumo do seu carro. O resultado mostra cada praça, a ida e a volta separadas e o total da viagem. Dá para copiar o link da simulação e mandar para quem vai dividir os custos com você.',
        ],
      },
    ],
  },
  {
    slug: 'pedagio-sao-paulo-curitiba',
    title: 'Pedágio de São Paulo a Curitiba: custo da viagem pela Régis Bittencourt',
    description: 'Quanto custa ir de São Paulo a Curitiba de carro: pedágio de R$ 25,80 por sentido, cerca de 404 km, 5h43 de viagem e custo total de ida e volta.',
    publishedAt: "2026-10-04",
    updatedAt: "2026-10-04",
    intro: 'A ligação entre São Paulo e Curitiba é feita principalmente pela Régis Bittencourt (BR-116). É uma rodovia federal com tarifas baixas por praça. Veja quanto custa o pedágio, o combustível e a viagem completa, e por que neste caso o pedágio pesa pouco no orçamento.',
    sections: [
      {
        heading: 'Resumo dos números',
        paragraphs: [
          'Todos os números deste guia saíram da calculadora do TrajetoFácil em 4 de outubro de 2026, para carro de passeio, com gasolina a R$ 5,89 por litro e consumo de 10 km/l. Pedágio é estimativa pelas tarifas oficiais vigentes; combustível muda conforme o preço que você paga e o consumo real do seu carro.',
        ],
        list: [
          'Ida (São Paulo → Curitiba): 403,8 km, cerca de 5h43, 6 praças, pedágio de R$ 25,80',
          'Volta (Curitiba → São Paulo): 402,1 km, cerca de 5h42, 6 praças, pedágio de R$ 25,80',
          'Combustível de ida e volta: R$ 474,68',
          'Pedágio de ida e volta: R$ 51,60',
          'Custo total da viagem: R$ 526,28, cerca de R$ 0,65 por km',
        ],
      },
      {
        heading: 'Um trecho com pedágio barato',
        paragraphs: [
          'São 6 praças em cada sentido, com média de R$ 4,30 por praça. As concessões federais mais antigas costumam ter tarifas menores que as das rodovias paulistas. Por isso, mesmo com mais de 400 km, o pedágio fica em cerca de 10% do custo da viagem.',
          'Aqui ida e volta têm o mesmo valor porque as praças cobram nos dois sentidos com a mesma tarifa. Ainda assim, vale calcular os dois sentidos: mudanças de traçado ou praças novas podem quebrar essa simetria.',
        ],
      },
      {
        heading: 'Comparação com outras rotas saindo de São Paulo',
        paragraphs: [
          'Para ter uma ideia, a viagem de São Paulo ao Rio tem quase a mesma distância, mas pedágio de R$ 141,60 na ida e volta, quase três vezes mais. Já São Paulo–Santos, com só 80 km, custa R$ 81,20 de pedágio. A distância sozinha não diz quanto se paga: o que manda é a tarifa de cada concessão.',
        ],
      },
      {
        heading: 'Para caminhões e ônibus',
        paragraphs: [
          'Na cobrança por eixo, um caminhão de 3 eixos paga em geral três vezes a tarifa básica por eixo em cada praça. Em rotas longas e baratas como esta, o combustível segue sendo o principal custo, mas multiplicar o pedágio pelos eixos muda bastante o valor do frete.',
        ],
      },
      {
        heading: 'Refaça a conta com os seus números',
        paragraphs: [
          'Abra a calculadora, digite origem e destino, escolha o seu veículo (moto, carro, ônibus ou caminhão com o número de eixos) e informe o preço do litro e o consumo do seu carro. O resultado mostra cada praça, a ida e a volta separadas e o total da viagem. Dá para copiar o link da simulação e mandar para quem vai dividir os custos com você.',
        ],
      },
    ],
  },
  {
    slug: 'pedagio-sao-paulo-belo-horizonte',
    title: 'Pedágio de São Paulo a Belo Horizonte: custo da viagem pela Fernão Dias',
    description: 'Quanto custa ir de São Paulo a Belo Horizonte de carro pela Fernão Dias: 9 praças, R$ 34,40 de pedágio por sentido, cerca de 583 km e custo total da viagem.',
    publishedAt: "2026-10-04",
    updatedAt: "2026-10-04",
    intro: 'A Fernão Dias (BR-381) liga São Paulo a Belo Horizonte em quase 600 km. Mesmo sendo uma viagem longa, o pedágio é baixo para a distância. Este guia mostra os números completos da viagem e onde está o maior custo.',
    sections: [
      {
        heading: 'Resumo dos números',
        paragraphs: [
          'Todos os números deste guia saíram da calculadora do TrajetoFácil em 4 de outubro de 2026, para carro de passeio, com gasolina a R$ 5,89 por litro e consumo de 10 km/l. Pedágio é estimativa pelas tarifas oficiais vigentes; combustível muda conforme o preço que você paga e o consumo real do seu carro.',
        ],
        list: [
          'Ida (São Paulo → Belo Horizonte): 582,7 km, cerca de 7h52, 9 praças, pedágio de R$ 34,40',
          'Volta (Belo Horizonte → São Paulo): 582,1 km, cerca de 7h51, 9 praças, pedágio de R$ 34,40',
          'Combustível de ida e volta: R$ 686,10',
          'Pedágio de ida e volta: R$ 68,80',
          'Custo total da viagem: R$ 754,90, cerca de R$ 0,65 por km',
        ],
      },
      {
        heading: 'Muitas praças, valores baixos',
        paragraphs: [
          'São 9 praças em cada sentido, com média abaixo de R$ 4 por praça. Na conta, pedágio fica em só 9% do custo. Nesta rota, quem quer economizar deve olhar primeiro o combustível: cada R$ 0,20 a mais no preço do litro soma cerca de R$ 23 na viagem de ida e volta.',
        ],
      },
      {
        heading: 'Planejando paradas',
        paragraphs: [
          'Quase 8 horas de direção por sentido pedem pelo menos uma parada longa. Se a viagem incluir uma cidade no meio do caminho, como Pouso Alegre ou Lavras, adicione a parada na calculadora: cada trecho é calculado separadamente, com distância, combustível e praças próprios, e o total soma todos eles.',
        ],
      },
      {
        heading: 'Refaça a conta com os seus números',
        paragraphs: [
          'Abra a calculadora, digite origem e destino, escolha o seu veículo (moto, carro, ônibus ou caminhão com o número de eixos) e informe o preço do litro e o consumo do seu carro. O resultado mostra cada praça, a ida e a volta separadas e o total da viagem. Dá para copiar o link da simulação e mandar para quem vai dividir os custos com você.',
        ],
      },
    ],
  },
  {
    slug: 'pedagio-sao-paulo-santos',
    title: 'Pedágio de São Paulo a Santos: quanto custa descer a serra',
    description: 'Quanto custa o pedágio de São Paulo a Santos pelo Sistema Anchieta-Imigrantes: R$ 40,60 por sentido para carro, cerca de 80 km e custo total da viagem.',
    publishedAt: "2026-10-04",
    updatedAt: "2026-10-04",
    intro: 'Descer para o litoral pelo Sistema Anchieta-Imigrantes é curto, mas o pedágio é um dos mais caros por quilômetro do país. Veja quanto se paga, por que a tarifa é tão alta e como isso muda o custo por km da viagem.',
    sections: [
      {
        heading: 'Resumo dos números',
        paragraphs: [
          'Todos os números deste guia saíram da calculadora do TrajetoFácil em 4 de outubro de 2026, para carro de passeio, com gasolina a R$ 5,89 por litro e consumo de 10 km/l. Pedágio é estimativa pelas tarifas oficiais vigentes; combustível muda conforme o preço que você paga e o consumo real do seu carro.',
        ],
        list: [
          'Ida (São Paulo → Santos): 81 km, cerca de 1h17, 1 praça, pedágio de R$ 40,60',
          'Volta (Santos → São Paulo): 79,2 km, cerca de 1h11, 1 praça, pedágio de R$ 40,60',
          'Combustível de ida e volta: R$ 94,31',
          'Pedágio de ida e volta: R$ 81,20',
          'Custo total da viagem: R$ 175,51, cerca de R$ 1,10 por km',
        ],
      },
      {
        heading: 'Uma praça que custa quase metade da viagem',
        paragraphs: [
          'É uma só praça por sentido, mas ela responde por 46% do custo total. Por isso o custo por km sobe para R$ 1,10, contra R$ 0,65 nas viagens de São Paulo a Curitiba ou a Belo Horizonte. A tarifa reflete o custo de construir e manter pistas na Serra do Mar.',
          'Em viagens curtas com pedágio caro, usar só o consumo do carro para estimar o custo dá um número bem abaixo do real. Sempre some o pedágio à parte.',
        ],
      },
      {
        heading: 'Feriados e operação descida',
        paragraphs: [
          'Em feriados a concessionária muda o sentido das pistas (operação descida e subida). A tarifa não muda, mas o tempo de viagem pode ser várias vezes maior que o estimado. Planeje o horário de saída com folga.',
        ],
      },
      {
        heading: 'Refaça a conta com os seus números',
        paragraphs: [
          'Abra a calculadora, digite origem e destino, escolha o seu veículo (moto, carro, ônibus ou caminhão com o número de eixos) e informe o preço do litro e o consumo do seu carro. O resultado mostra cada praça, a ida e a volta separadas e o total da viagem. Dá para copiar o link da simulação e mandar para quem vai dividir os custos com você.',
        ],
      },
    ],
  },
  {
    slug: 'pedagio-campinas-ribeirao-preto',
    title: 'Pedágio de Campinas a Ribeirão Preto: custo da viagem pela Anhanguera',
    description: 'Quanto custa ir de Campinas a Ribeirão Preto de carro: 4 praças, R$ 46,10 de pedágio por sentido, cerca de 222 km e custo total de ida e volta.',
    publishedAt: "2026-10-04",
    updatedAt: "2026-10-04",
    intro: 'O interior paulista tem as rodovias mais bem avaliadas do país, e também pedágios mais caros por quilômetro que as federais. A viagem de Campinas a Ribeirão Preto pela Anhanguera (SP-330) é um bom exemplo de como isso aparece na conta.',
    sections: [
      {
        heading: 'Resumo dos números',
        paragraphs: [
          'Todos os números deste guia saíram da calculadora do TrajetoFácil em 4 de outubro de 2026, para carro de passeio, com gasolina a R$ 5,89 por litro e consumo de 10 km/l. Pedágio é estimativa pelas tarifas oficiais vigentes; combustível muda conforme o preço que você paga e o consumo real do seu carro.',
        ],
        list: [
          'Ida (Campinas → Ribeirão Preto): 222,3 km, cerca de 2h48, 4 praças, pedágio de R$ 46,10',
          'Volta (Ribeirão Preto → Campinas): 222,4 km, cerca de 2h46, 4 praças, pedágio de R$ 46,10',
          'Combustível de ida e volta: R$ 261,90',
          'Pedágio de ida e volta: R$ 92,20',
          'Custo total da viagem: R$ 354,10, cerca de R$ 0,80 por km',
        ],
      },
      {
        heading: 'Rodovias paulistas e o preço por km',
        paragraphs: [
          'São R$ 46,10 em 222 km, cerca de R$ 0,21 por km só de pedágio. Na Régis Bittencourt, entre São Paulo e Curitiba, a mesma conta dá cerca de R$ 0,06 por km. A diferença vem dos contratos de concessão: as estaduais paulistas, reguladas pela ARTESP, têm tarifas maiores e mais praças por trecho.',
          'Nessa viagem o pedágio chega a 26% do custo total. Para quem faz a rota toda semana a trabalho, isso dá mais de R$ 4.700 por ano só de pedágio.',
        ],
      },
      {
        heading: 'Usando para cobrar um serviço',
        paragraphs: [
          'Representantes comerciais e prestadores de serviço do interior costumam cobrar o deslocamento por km. Com R$ 0,80 por km de custo, cobrar R$ 1,00 por km deixa uma margem de 25%. Na calculadora, o multiplicador de repasse faz essa conta e mostra direto o valor a cobrar.',
        ],
      },
      {
        heading: 'Refaça a conta com os seus números',
        paragraphs: [
          'Abra a calculadora, digite origem e destino, escolha o seu veículo (moto, carro, ônibus ou caminhão com o número de eixos) e informe o preço do litro e o consumo do seu carro. O resultado mostra cada praça, a ida e a volta separadas e o total da viagem. Dá para copiar o link da simulação e mandar para quem vai dividir os custos com você.',
        ],
      },
    ],
  },
];

export function getGuide(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}
